import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * Partner Portal Login Page
 *
 * Features:
 * - Google OAuth login
 * - Email/password login
 * - Passkey authentication
 * - Registration links for new users
 * - Accessible form with proper ARIA attributes
 *
 * Brand: Partner Portal uses Purple/Blue gradient theme
 * Colors: #282828 (signal-black), #4c1d95 (deep-purple), #1283EB (electric-blue)
 */
export function Login() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeySupported, setPasskeySupported] = useState(false);

  // Check if WebAuthn/passkey is supported
  useEffect(() => {
    const checkPasskeySupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available =
            await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setPasskeySupported(available);
        } catch {
          setPasskeySupported(false);
        }
      }
    };
    checkPasskeySupport();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const returnTo = searchParams.get("returnTo") || "/dashboard";
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, searchParams]);

  // Check for OAuth errors in URL
  useEffect(() => {
    const oauthError = searchParams.get("error");
    if (oauthError === "oauth_failed") {
      setError("Failed to authenticate with Google. Please try again.");
    }
  }, [searchParams]);

  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Login failed");
      }

      // Success - navigate to dashboard
      const returnTo = searchParams.get("returnTo") || "/dashboard";
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An error occurred during login",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setIsGoogleLoading(true);

    try {
      // Use admin backend for Google OAuth with explicit redirect_origin for portal
      // This allows portal.aiia.io (on Cloudflare) to use the admin backend's OAuth
      const currentOrigin = window.location.hostname;
      const authBaseUrl = import.meta.env.VITE_AUTH_API_URL || "https://admin.aiia.io";
      const response = await fetch(
        `${authBaseUrl}/auth/google/login?redirect_origin=${encodeURIComponent(currentOrigin)}`,
        {
          credentials: "include",
          mode: "cors",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to initiate Google login");
      }

      const { auth_url, state } = await response.json();

      // Store state in sessionStorage for validation
      sessionStorage.setItem("oauth_state", state);

      // Redirect to Google OAuth
      window.location.href = auth_url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to start Google login",
      );
      setIsGoogleLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError(null);
    setIsPasskeyLoading(true);

    try {
      // Use admin backend for passkey auth with explicit redirect_origin for portal
      const currentOrigin = window.location.hostname;
      const authBaseUrl = import.meta.env.VITE_AUTH_API_URL || "https://admin.aiia.io";
      const response = await fetch(
        `${authBaseUrl}/auth/passkey/login?redirect_origin=${encodeURIComponent(currentOrigin)}`,
        {
          credentials: "include",
          mode: "cors",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to initiate passkey login");
      }

      const { auth_url, state } = await response.json();

      // Store state in sessionStorage for CSRF protection
      sessionStorage.setItem("oauth_state", state);

      // Redirect to passkey auth
      window.location.href = auth_url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Passkey authentication failed",
      );
      setIsPasskeyLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      {/* Enhanced gradient background with electric blue accent */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1283EB]/15 via-[#4c1d95]/30 to-[#282828]" />
        <div className="absolute inset-0 bg-gradient-to-tl from-[#1283EB]/10 via-transparent to-transparent" />
      </div>

      <div className="max-w-md w-full space-y-8 p-10 bg-[#1a1a1a]/90 backdrop-blur-sm rounded-2xl border border-[#1283EB]/20 shadow-2xl mx-4">
        {/* Logo/Header */}
        <div className="text-center">
          <img
            src="/aiia-logo-white.svg"
            alt="aiia logo"
            className="h-14 mx-auto mb-6"
          />
          <h1 className="text-xl text-white font-semibold">
            Welcome to aiia
          </h1>
          <p className="mt-2 text-sm text-white/60">
            Sign in to access your workspace
          </p>
        </div>

        {/* Error Alert with aria-live for screen readers */}
        <div
          role="alert"
          aria-live="polite"
          aria-atomic="true"
          className={error ? "" : "sr-only"}
        >
          {error && (
            <Alert
              variant="destructive"
              className="bg-red-950/50 border-red-800 text-red-200"
            >
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Google OAuth Button */}
        <div className="space-y-3">
          <Button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading || isLoading || isPasskeyLoading}
            className="w-full h-13 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-3 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
          >
            {isGoogleLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Connecting to Google...</span>
              </>
            ) : (
              <>
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </Button>

          {/* Passkey Button - only shown if WebAuthn is supported */}
          {passkeySupported && (
            <Button
              type="button"
              onClick={handlePasskeyLogin}
              disabled={isPasskeyLoading || isLoading || isGoogleLoading}
              variant="outline"
              className="w-full h-13 bg-transparent hover:bg-white/5 text-white font-medium rounded-lg border border-white/20 transition-all duration-200 flex items-center justify-center gap-3 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
            >
              {isPasskeyLoading ? (
                <>
                  <Loader2
                    className="h-5 w-5 animate-spin"
                    aria-hidden="true"
                  />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                    />
                  </svg>
                  <span>Sign in with Passkey</span>
                </>
              )}
            </Button>
          )}
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-[#1a1a1a]/90 text-white/50">
              Or continue with email
            </span>
          </div>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailPasswordLogin} className="space-y-5">
          {/* Email Input */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-white/80 mb-1.5"
            >
              Email address
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              aria-required="true"
              aria-describedby={error ? "login-error" : undefined}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-12 bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/40 focus:border-[#1283EB] focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a] transition-all"
              disabled={isLoading || isGoogleLoading || isPasskeyLoading}
            />
          </div>

          {/* Password Input */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-white/80 mb-1.5"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                aria-required="true"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-12 bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/40 focus:border-[#1283EB] focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a] pr-12 transition-all"
                disabled={isLoading || isGoogleLoading || isPasskeyLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors focus:outline-none focus:text-white"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Eye className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-[#1283EB] hover:text-[#1283EB]/80 transition-colors focus:outline-none focus:underline"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading || isGoogleLoading || isPasskeyLoading}
            className="w-full h-13 bg-[#1283EB] hover:bg-[#1283EB]/90 text-white font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
          >
            {isLoading ? (
              <>
                <Loader2
                  className="h-5 w-5 mr-2 animate-spin"
                  aria-hidden="true"
                />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="text-center space-y-4">
          {/* Registration Links */}
          <div className="pt-4 border-t border-white/10 space-y-2">
            <p className="text-sm text-white/60">
              New to aiia?{" "}
              <Link
                to="/register-solo"
                className="text-[#1283EB] hover:text-[#1283EB]/80 font-medium transition-colors"
              >
                Start your free trial
              </Link>
            </p>
            <p className="text-sm text-white/50">
              Have an invite code?{" "}
              <Link
                to="/register"
                className="text-[#1283EB]/80 hover:text-[#1283EB] transition-colors"
              >
                Register as Partner/Client
              </Link>
            </p>
          </div>

          {/* Legal Links */}
          <div className="pt-3 text-xs text-white/40">
            <Link
              to="/privacy"
              className="hover:text-white/60 transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="mx-2">|</span>
            <Link to="/terms" className="hover:text-white/60 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
